import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import type { TFunction } from "i18next";
import type { SourcingEventDto } from "../../../autogen/bff-api";
import { isDpsCompetition, isSchemeProcedure } from "../../sourcing-events/buyer/wizard/utils/procedure";
import { isNegotiationWithoutNotice } from "../../sourcing-events/buyer/wizard/utils/negotiationWithoutNotice";
import type { TimelinePhaseKey } from "./deriveTimelinePhase";

// Compact labels for the details-page progress stepper. English keys; Norwegian values already
// exist in locales (Kunngjøring / Tilbudsfrist / Evaluering / Tildeling / Karens / Kontrakt).
// Listed so langen keeps them — keys are referenced dynamically below:
// t('Announcement') t('Bid deadline') t('Evaluation') t('Award') t('Standstill') t('Contract')
// t('Request deadline') t('Qualification & selection') t('Result') t('Deadline for entries')
const PHASE_LABEL_KEYS: Record<TimelinePhaseKey, string> = {
  announcement: "Announcement",
  requestDeadline: "Request deadline",
  qualification: "Qualification & selection",
  questionDeadline: "Question deadline",
  bidDeadline: "Bid deadline",
  evaluationStart: "Evaluation",
  awardDecision: "Award",
  standstillExpiry: "Standstill",
  contractSigning: "Contract",
  resultNotice: "Result",
};

// The same two keys mean different things on each side of a DPS (FOA §§ 26-6, 26-7).
// t('Admission open') t('Competition opens')
const dpsLabelKey = (key: TimelinePhaseKey, event: SourcingEventDto): string | null => {
  const procedureType = event.eventContext.type === "Public" ? event.eventContext.procedureType : null;
  if (isSchemeProcedure(procedureType) && key === "requestDeadline") return "Admission open";
  if (isDpsCompetition(procedureType) && key === "announcement") return "Competition opens";
  return null;
};

// Tilbudsfrist keeps its name while showing a revised-offer deadline: the round count sits right
// under it, so the step is read inside the negotiation group rather than on its own.
export const phaseLabel = (
  key: TimelinePhaseKey,
  event: SourcingEventDto,
  t: TFunction,
): string => {
  if (key === "announcement" && event.eventContext.type === "Private") return t("Published");
  if (
    key === "announcement" &&
    event.eventContext.type === "Public" &&
    isNegotiationWithoutNotice(event.eventContext.procedureType)
  ) {
    return t("Invitations sent");
  }
  if (key === "bidDeadline" && eventIsDesignContest(event)) return t("Deadline for entries");
  return t(dpsLabelKey(key, event) ?? PHASE_LABEL_KEYS[key]);
};
